import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { currentRole, isLivyAdmin, livyUnavailableMessage } from 'src/components/livy/access';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, LinearProgress, Tab, Tabs, Box, Button, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { LivyMarkdown } from 'src/components/livy/LivyMarkdown';
import { livyConfig } from 'src/lib/livyTransport';
import { livyJson } from 'src/lib/livyApi';
import { ArchitectureTab } from './tabs/Architecture';
import { ToolsTab } from './tabs/Tools';
import { FeedbackTab } from './tabs/Feedback';
import { SupportTab } from './tabs/Support';
import { LimitsTab } from './tabs/Limits';
import { RequestsTab } from './tabs/Requests';
import { LivyTheme } from 'src/components/livy/LivyTheme';

/** One request now answers both panels, so the list arrives with the roll-up it belongs to. */

/**
 * How far back to look. 400 is the operational tier's whole retention, which is what "all time"
 * honestly means here: the transcript tier keeps 90 days by design and nothing on this page reads
 * it.
 */
const WINDOWS = [7, 30, 90, 400];
const money = n => "$".concat(n.toFixed(4));
const entries = function () {
  let o = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Object.entries(o).sort((a, b) => b[1] - a[1]);
};
const Section = _ref => {
  let title = _ref.title,
    children = _ref.children;
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      mb: '20px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11,
      letterSpacing: '.07em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: '8px'
    }
  }, title), children);
};
const Rows = _ref2 => {
  let data = _ref2.data,
    format = _ref2.format,
    empty = _ref2.empty,
    _ref2$tone = _ref2.tone,
    tone = _ref2$tone === void 0 ? 'primary' : _ref2$tone;
  if (data.length === 0) {
    return /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "text.secondary"
    }, empty);
  }
  // A column of numbers makes the reader do the comparing. The bar is the comparison, so the
  // outlier is visible before anything is read.
  const max = Math.max(...data.map(_ref3 => {
    let _ref4 = _slicedToArray(_ref3, 2),
      v = _ref4[1];
    return v;
  })) || 1;
  return /*#__PURE__*/React.createElement(Stack, {
    spacing: "9px"
  }, data.slice(0, 8).map(_ref5 => {
    let _ref6 = _slicedToArray(_ref5, 2),
      key = _ref6[0],
      value = _ref6[1];
    return /*#__PURE__*/React.createElement(Box, {
      key: key
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      justifyContent: "space-between",
      sx: {
        fontSize: 12.5,
        mb: '3px'
      }
    }, /*#__PURE__*/React.createElement(Box, {
      component: "span",
      sx: {
        color: 'text.secondary',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        mr: '10px'
      }
    }, key), /*#__PURE__*/React.createElement(Box, {
      component: "span",
      sx: {
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 500
      }
    }, format(value))), /*#__PURE__*/React.createElement(LinearProgress, {
      variant: "determinate",
      value: value / max * 100,
      color: tone,
      sx: {
        height: 4,
        borderRadius: 2,
        bgcolor: 'action.hover'
      }
    }));
  }));
};

/**
 * The operational tier, made readable. Logs nobody reads are theatre, and the numbers that decide
 * whether this is worth running, cost per user, which tools actually fire, what is failing, only
 * exist if someone can see them.
 *
 * The transcript is deliberately not on this page by default. Reading a customer's conversation is
 * a separate, permissioned action, and asking for it is itself recorded.
 */
const TABS = [{
  value: 'usage',
  label: 'Usage'
}, {
  value: 'support',
  label: 'Support queue'
}, {
  value: 'feedback',
  label: 'Feedback'
}, {
  value: 'tools',
  label: 'Tools'
}, {
  value: 'limits',
  label: 'Limits'
}, {
  value: 'requests',
  label: 'Requests'
}, {
  value: 'architecture',
  label: 'AI architecture'
}];
function LivyAdminPageBody() {
  var _stats$days;
  const isAdmin = isLivyAdmin(currentRole());
  const configured = Boolean(livyConfig());
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    stats = _useState2[0],
    setStats = _useState2[1];
  const _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    conversations = _useState4[0],
    setConversations = _useState4[1];
  const _useState5 = useState({}),
    _useState6 = _slicedToArray(_useState5, 2),
    proposals = _useState6[0],
    setProposals = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    openId = _useState8[0],
    setOpenId = _useState8[1];
  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    rows = _useState0[0],
    setRows = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    transcriptShown = _useState10[0],
    setTranscriptShown = _useState10[1];
  const _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    error = _useState12[0],
    setError = _useState12[1];
  // The retired /livy/support route redirects here with ?tab=support, so the deep link lands on
  // the queue rather than on Usage.
  const _useState13 = useState(() => {
      const wanted = new URLSearchParams(window.location.search).get('tab');
      return TABS.some(t => t.value === wanted) ? wanted : 'usage';
    }),
    _useState14 = _slicedToArray(_useState13, 2),
    tab = _useState14[0],
    setTab = _useState14[1];
  const _useState15 = useState(30),
    _useState16 = _slicedToArray(_useState15, 2),
    days = _useState16[0],
    setDays = _useState16[1];
  // What the server said it actually did. Anything that is not exactly 'platform' - including an
  // older proxy that answers without the field - is one company, so a frontend running ahead of
  // the proxy never labels one company's rows as every company's.
  const appliedPlatform = (stats === null || stats === void 0 ? void 0 : stats.scope) === 'platform';
  // Every company by default. The page is admin-only and the question it exists to answer is
  // "who is using Livy", which one company's partition can never answer - staff impersonate by
  // swapping company, so a person's own turns are scattered across every tenant they visited.
  const _useState17 = useState(true),
    _useState18 = _slicedToArray(_useState17, 2),
    platform = _useState18[0],
    setPlatform = _useState18[1];

  // Which request is the current one. Switching window or scope starts another before the last
  // has answered, and a slow platform read landing after a newer company read would otherwise
  // paint the view the reader just moved away from.
  const request = useRef(0);
  const load = useCallback(async () => {
    const generation = ++request.current;
    try {
      var _s$recent;
      const query = "?days=".concat(days, "&scope=").concat(platform ? 'platform' : 'company');
      const _await$Promise$all = await Promise.all([livyJson("/logs/stats".concat(query)), livyJson('/logs/proposals')]),
        _await$Promise$all2 = _slicedToArray(_await$Promise$all, 2),
        s = _await$Promise$all2[0],
        p = _await$Promise$all2[1];
      if (generation !== request.current) return;
      setStats(s);
      setConversations((_s$recent = s.recent) !== null && _s$recent !== void 0 ? _s$recent : []);
      setProposals(p.counts);
    } catch (e) {
      if (generation === request.current) setError(e.message);
    }
  }, [days, platform]);
  useEffect(() => {
    if (isAdmin && configured) void load();
  }, [isAdmin, configured, load]);
  const open = async (id, withTranscript) => {
    setError(null);
    try {
      const _await$livyJson = await livyJson("/logs/conversations/".concat(id).concat(withTranscript ? '?transcript=true' : '')),
        items = _await$livyJson.items;
      setOpenId(id);
      setRows(items);
      setTranscriptShown(withTranscript);
    } catch (e) {
      setError(e.message);
    }
  };

  // A request links back to the conversation it came from, so the link has to land on it opened.
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get('conversation');
    if (isAdmin && configured && wanted) void open(wanted, false);
    // Deep link is read once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, configured]);
  if (!isAdmin || !configured) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        p: '24px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1"
    }, livyUnavailableMessage));
  }
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      p: '20px',
      overflowY: 'auto',
      height: '100%'
    }
  }, /*#__PURE__*/React.createElement(Tabs, {
    value: tab,
    onChange: (_, next) => setTab(next),
    variant: "scrollable",
    scrollButtons: "auto",
    sx: {
      borderBottom: 1,
      borderColor: 'divider',
      mb: '8px',
      minHeight: 40
    }
  }, TABS.map(t => /*#__PURE__*/React.createElement(Tab, {
    key: t.value,
    value: t.value,
    label: t.label,
    sx: {
      textTransform: 'none',
      minHeight: 40,
      fontSize: 13.5
    }
  }))), tab === 'architecture' && /*#__PURE__*/React.createElement(ArchitectureTab, null), tab === 'tools' && /*#__PURE__*/React.createElement(ToolsTab, null), tab === 'feedback' && /*#__PURE__*/React.createElement(FeedbackTab, null), tab === 'support' && /*#__PURE__*/React.createElement(SupportTab, null), tab === 'limits' && /*#__PURE__*/React.createElement(LimitsTab, null), tab === 'requests' && /*#__PURE__*/React.createElement(RequestsTab, null), tab === 'usage' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    alignItems: "center",
    justifyContent: "space-between",
    sx: {
      mb: '14px',
      mt: '10px',
      flexWrap: 'wrap',
      rowGap: '8px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 18,
      fontWeight: 600
    }
  }, "Livy activity", /*#__PURE__*/React.createElement(Typography, {
    component: "span",
    sx: {
      fontSize: 13,
      fontWeight: 400,
      color: 'text.secondary'
    }
  }, " \xB7 ".concat(appliedPlatform ? 'all companies' : 'this company', " \xB7 last ").concat((_stats$days = stats === null || stats === void 0 ? void 0 : stats.days) !== null && _stats$days !== void 0 ? _stats$days : days, " days"))), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "6px",
    alignItems: "center",
    sx: {
      flexWrap: 'wrap',
      rowGap: '6px'
    }
  }, WINDOWS.map(w => /*#__PURE__*/React.createElement(Chip, {
    key: w,
    size: "small",
    label: "".concat(w, "d"),
    onClick: () => setDays(w),
    variant: days === w ? 'filled' : 'outlined',
    color: days === w ? 'primary' : 'default'
  })), /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    label: platform ? 'All companies' : 'This company',
    onClick: () => setPlatform(v => !v),
    variant: "outlined",
    color: "primary"
  }), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    onClick: load,
    sx: {
      textTransform: 'none'
    }
  }, "Refresh"))), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      mb: '14px'
    }
  }, error), (stats === null || stats === void 0 ? void 0 : stats.truncated) && /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    variant: "outlined",
    sx: {
      mb: '14px'
    }
  }, "This window holds more activity than one read returns. The numbers below are a floor. Narrow the window for an exact answer."), stats && /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'grid',
      gap: '10px',
      gridTemplateColumns: {
        xs: '1fr 1fr',
        md: 'repeat(5, 1fr)'
      },
      mb: '20px'
    }
  }, [{
    label: 'Conversations',
    value: String(stats.conversations)
  }, {
    label: 'Turns',
    value: String(stats.turns)
  }, {
    label: 'Cost',
    value: money(stats.costUsd)
  },
  // Two of these are health, not volume, so they say so rather than sitting in the
  // row looking like every other number.
  {
    label: 'Errors',
    value: String(stats.errors),
    tone: stats.errors > 0 ? 'error' : undefined
  }, {
    label: 'Slowest turn',
    value: "".concat((stats.slowestMs / 1000).toFixed(1), "s"),
    tone: stats.slowestMs > 25000 ? 'warning' : undefined
  }].map(_ref7 => {
    let label = _ref7.label,
      value = _ref7.value,
      tone = _ref7.tone;
    return /*#__PURE__*/React.createElement(Box, {
      key: label,
      sx: {
        p: '12px 14px',
        border: 1,
        borderColor: tone ? "".concat(tone, ".main") : 'divider',
        borderRadius: '10px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 10.5,
        letterSpacing: '.07em',
        textTransform: 'uppercase',
        color: 'text.secondary'
      }
    }, label), /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 20,
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        color: tone ? "".concat(tone, ".main") : 'text.primary'
      }
    }, value));
  })), /*#__PURE__*/React.createElement(Stack, {
    direction: {
      xs: 'column',
      lg: 'row'
    },
    spacing: "20px",
    alignItems: "flex-start"
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      flex: 1,
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement(Section, {
    title: "Cost by day (UTC)"
  }, /*#__PURE__*/React.createElement(Rows, {
    data: entries(stats === null || stats === void 0 ? void 0 : stats.byDay),
    format: money,
    empty: "No turns yet"
  })), /*#__PURE__*/React.createElement(Section, {
    title: "Cost by user"
  }, /*#__PURE__*/React.createElement(Rows, {
    data: entries(stats === null || stats === void 0 ? void 0 : stats.byUser),
    format: money,
    empty: "No turns yet"
  })), appliedPlatform && /*#__PURE__*/React.createElement(Section, {
    title: "Cost by company"
  }, /*#__PURE__*/React.createElement(Rows, {
    data: entries(stats === null || stats === void 0 ? void 0 : stats.byCompany),
    format: money,
    empty: "No turns yet"
  })), /*#__PURE__*/React.createElement(Section, {
    title: "Tools used"
  }, /*#__PURE__*/React.createElement(Rows, {
    data: entries(stats === null || stats === void 0 ? void 0 : stats.tools),
    format: String,
    empty: "No tool calls yet"
  })), /*#__PURE__*/React.createElement(Section, {
    title: "Errors"
  }, /*#__PURE__*/React.createElement(Rows, {
    data: entries(stats === null || stats === void 0 ? void 0 : stats.errorCodes),
    format: String,
    empty: "No errors",
    tone: "error"
  })), /*#__PURE__*/React.createElement(Section, {
    title: "Proposals"
  }, /*#__PURE__*/React.createElement(Rows, {
    data: entries(proposals),
    format: String,
    empty: "None raised"
  }))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flex: 1.4,
      minWidth: 0,
      width: '100%'
    }
  }, /*#__PURE__*/React.createElement(Section, {
    title: "Conversations"
  }, conversations.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "Nothing yet") : /*#__PURE__*/React.createElement(Table, {
    size: "small",
    sx: {
      '& td, & th': {
        px: '10px',
        py: '6px',
        fontSize: 13
      }
    }
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, null, "Last activity"), /*#__PURE__*/React.createElement(TableCell, null, "Who"), appliedPlatform && /*#__PURE__*/React.createElement(TableCell, null, "Company"), /*#__PURE__*/React.createElement(TableCell, null, "Surface"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Turns"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Cost"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }))), /*#__PURE__*/React.createElement(TableBody, null, conversations.map(c => {
    var _c$userId, _c$companyId, _c$surface, _c$turns;
    return /*#__PURE__*/React.createElement(TableRow, {
      key: c.conversationId,
      selected: openId === c.conversationId
    }, /*#__PURE__*/React.createElement(TableCell, null, c.lastActivityAt ? new Date(c.lastActivityAt).toLocaleString() : '—'), /*#__PURE__*/React.createElement(TableCell, {
      sx: {
        maxWidth: 180,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }
    }, (_c$userId = c.userId) !== null && _c$userId !== void 0 ? _c$userId : '—'), appliedPlatform && /*#__PURE__*/React.createElement(TableCell, {
      sx: {
        maxWidth: 150,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }
    }, (_c$companyId = c.companyId) !== null && _c$companyId !== void 0 ? _c$companyId : '—'), /*#__PURE__*/React.createElement(TableCell, null, (_c$surface = c.surface) !== null && _c$surface !== void 0 ? _c$surface : 'admin'), /*#__PURE__*/React.createElement(TableCell, {
      align: "right"
    }, (_c$turns = c.turns) !== null && _c$turns !== void 0 ? _c$turns : '—'), /*#__PURE__*/React.createElement(TableCell, {
      align: "right"
    }, c.costUsd !== undefined ? money(c.costUsd) : '—'), /*#__PURE__*/React.createElement(TableCell, {
      align: "right"
    }, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => open(c.conversationId, false),
      sx: {
        textTransform: 'none'
      }
    }, "Open")));
  })))), openId && /*#__PURE__*/React.createElement(Section, {
    title: "Conversation ".concat(openId.slice(0, 8))
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    sx: {
      mb: '10px'
    }
  }, !transcriptShown && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    color: "warning",
    variant: "outlined",
    onClick: () => open(openId, true),
    sx: {
      textTransform: 'none'
    }
  }, "Show transcript"), transcriptShown && /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    color: "warning",
    variant: "outlined",
    label: "Transcript access recorded"
  })), rows.filter(r => {
    var _r$sk;
    return (_r$sk = r.sk) === null || _r$sk === void 0 ? void 0 : _r$sk.startsWith('turn#');
  }).map(r => {
    var _r$toolCalls;
    return /*#__PURE__*/React.createElement(Box, {
      key: r.sk,
      sx: {
        mb: '8px',
        fontSize: 12.5,
        color: 'text.secondary'
      }
    }, r.at ? new Date(r.at).toLocaleTimeString() : '', " \xB7 ", r.model, " \xB7", ' ', r.costUsd !== undefined ? money(r.costUsd) : '-', " \xB7", ' ', r.durationMs !== undefined ? "".concat((r.durationMs / 1000).toFixed(1), "s") : '-', r.error ? " \xB7 error ".concat(r.error) : '', (_r$toolCalls = r.toolCalls) !== null && _r$toolCalls !== void 0 && _r$toolCalls.length ? " \xB7 ".concat(r.toolCalls.map(t => t.name).join(', ')) : '');
  }), transcriptShown && rows.filter(r => r.tier === 'transcript').map(r => {
    var _r$content;
    return /*#__PURE__*/React.createElement(Box, {
      key: r.sk,
      sx: {
        mt: '10px',
        p: '10px 12px',
        border: 1,
        borderColor: 'divider',
        borderRadius: '8px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 10.5,
        textTransform: 'uppercase',
        letterSpacing: '.07em',
        color: 'text.secondary'
      }
    }, r.role), /*#__PURE__*/React.createElement(LivyMarkdown, {
      content: (_r$content = r.content) !== null && _r$content !== void 0 ? _r$content : ''
    }));
  }))))));
}

/** Ripples off inside Livy: see LivyTheme. */
export function LivyAdminPage() {
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyAdminPageBody, null));
}